// Sources & calculations — where every number on the Readout comes from and
// how it is computed. Rendered from GET /admin/readout/sources, which is built
// from the same code the live readers run (a backend test asserts coverage),
// so this tab cannot drift from the numbers.

import { useEffect, useState } from 'react';
import { readoutService, type ReadoutSystem, type SourceCatalog } from '../../services/readout';
import styles from './ReadoutPage.module.css';

interface Props {
  systems: ReadoutSystem[];
  onSelect: (systemKey: string) => void;
}

const WINDOW_LABELS: Record<string, string> = {
  window: 'selected window',
  now: 'right now',
  none: 'not time-bound',
};

export function SourcesTab({ systems, onSelect }: Props) {
  const [result, setResult] = useState<{ catalog: SourceCatalog | null; error: string | null } | null>(null);

  useEffect(() => {
    let alive = true;
    readoutService
      .getSources()
      .then((c) => alive && setResult({ catalog: c, error: null }))
      .catch((err: unknown) =>
        alive && setResult({ catalog: null, error: err instanceof Error ? err.message : 'Failed to load the sources catalog' }),
      );
    return () => {
      alive = false;
    };
  }, []);

  if (!result) return <div className={styles.loading}>Loading sources…</div>;
  if (result.error || !result.catalog) return <div className={styles.error}>{result.error}</div>;
  const catalog = result.catalog;
  const nameOf = (key: string) => systems.find((s) => s.key === key)?.name ?? key;

  return (
    <div data-testid="sources-tab">
      <div className={styles.panel}>
        <p className={styles.panelTitle}>How to read this page</p>
        <p className={styles.narrativeBody} style={{ margin: 0 }}>
          Two kinds of numbers. <strong>Live numbers</strong> are computed from our own tables every time the page loads,
          for the window you pick at the top. <strong>Recorded</strong> content (the written readout, the ledger of costs /
          funding / benefits, and extra metrics) is what the readout curator agent wrote down, with its evidence.{' '}
          {catalog.window_note}
        </p>
      </div>

      <div className={styles.panel}>
        <p className={styles.panelTitle}>Money rules</p>
        <RuleTable rules={catalog.money_rules} />
      </div>

      <div className={styles.panel}>
        <p className={styles.panelTitle}>How spend and activity are attributed to a system</p>
        <RuleTable rules={catalog.attribution} />
      </div>

      {catalog.readers.map((r) => (
        <div className={styles.panel} key={r.reader} data-testid={`sources-${r.reader}`}>
          <p className={styles.panelTitle}>
            Live: {r.label}
            <span className={styles.panelNote}>
              {r.systems.length === 0
                ? 'no system yet'
                : r.systems.map((k, i) => (
                    <span key={k}>
                      {i > 0 && ', '}
                      <a
                        href={`?system=${k}`}
                        onClick={(e) => {
                          e.preventDefault();
                          onSelect(k);
                        }}
                      >
                        {nameOf(k)}
                      </a>
                    </span>
                  ))}
            </span>
          </p>
          <p className={styles.sub} style={{ marginTop: 0 }}>{r.description}</p>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Number</th>
                <th>Comes from</th>
                <th>How it is calculated</th>
                <th>Window</th>
              </tr>
            </thead>
            <tbody>
              {r.metrics.map((m) => (
                <tr key={m.metric_key}>
                  <td>
                    <strong>{m.label}</strong>
                    <div className={styles.sub}>{m.metric_key}</div>
                  </td>
                  <td>
                    <code>{m.source}</code>
                  </td>
                  <td>
                    {m.calculation}
                    {m.caveats && <div className={`${styles.sub} ${styles.warn}`}>{m.caveats}</div>}
                  </td>
                  <td className={styles.sub}>{WINDOW_LABELS[m.window] ?? m.window}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <div className={styles.panel}>
        <p className={styles.panelTitle}>Recorded by the curator</p>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Table</th>
              <th>What it holds</th>
              <th>Who writes it</th>
              <th>Shown as</th>
            </tr>
          </thead>
          <tbody>
            {catalog.recorded.map((rec) => (
              <tr key={rec.table}>
                <td>
                  <code>{rec.table}</code>
                </td>
                <td>{rec.what}</td>
                <td>{rec.written_by}</td>
                <td>{rec.shown_as}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.panel}>
        <p className={styles.panelTitle}>Freshness</p>
        <RuleTable rules={catalog.freshness} />
      </div>
    </div>
  );
}

function RuleTable({ rules }: { rules: { name: string; rule: string }[] }) {
  return (
    <table className={styles.table}>
      <tbody>
        {rules.map((r) => (
          <tr key={r.name}>
            <td style={{ width: '22%' }}>
              <strong>{r.name}</strong>
            </td>
            <td>{r.rule}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
